/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { handleLoading } from '@app/common/rxjs-operators';
import { extractError } from '@app/common/utils';
import { toggleBanner } from '@app/core/ngrx/actions/closed-banners.actions';
import { AuthNotificationType } from '@app/public/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_CANCEL, ROUTER_ERROR, ROUTER_NAVIGATED, ROUTER_REQUEST } from '@ngrx/router-store';
import { EMAIL_NOT_CONFIRMED_ERROR_CODE } from '@shared/constants/api-error-codes';
import { of, timer } from 'rxjs';
import { catchError, filter, map, pluck, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../services';
import {
  emailNeedsConfirmation,
  getUser,
  getUserFailure,
  getUserSuccess,
  initUser,
  login,
  logout,
  logoutFailure,
  logoutSuccess,
  remindPassword,
  remindPasswordFailure,
  remindPasswordSuccess,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
  sendActivation,
  sendActivationFailure,
  sendActivationSuccess,
  signin,
  signinFailure,
  signinSuccess,
  signup,
  signupFailure,
  signupSuccess,
} from './actions';

@Injectable()
export class AuthEffects {
  constructor(
    private authService: AuthService,
    private actions$: Actions,
    private router: Router,
  ) {}

  initLoading$ = createEffect(() =>
    this.actions$.pipe(
      handleLoading([
        { start: logout, end: [logoutSuccess, logoutFailure] },
        { start: initUser, end: [login, getUserFailure] },
        { start: signin, end: [signinSuccess, emailNeedsConfirmation, signinFailure] },
        { start: signup, end: [signupSuccess, emailNeedsConfirmation, signupFailure] },
        { start: remindPassword, end: [remindPasswordSuccess, remindPasswordFailure] },
        { start: resetPassword, end: [resetPasswordSuccess, resetPasswordFailure] },
        { start: sendActivation, end: [sendActivationSuccess, sendActivationFailure] },
        { start: ROUTER_REQUEST, end: [ROUTER_NAVIGATED, ROUTER_CANCEL, ROUTER_ERROR] },
      ]),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      switchMap(() =>
        this.authService.logout().pipe(
          map(() => logoutSuccess()),
          catchError((error) =>
            of(
              logoutFailure({
                error: extractError(error, `Failed to logout`),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  initUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initUser),
      switchMap((action) =>
        this.authService.getUser().pipe(
          map((user) => login({ user })),
          catchError((error) =>
            of(
              getUserFailure({
                error: extractError(error, `Can't get current user`),
                silent: error.status === 403 ? true : false,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  signin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signin),
      pluck('data'),
      switchMap((data) =>
        this.authService.signin(data).pipe(
          switchMap(() => [signinSuccess(), initUser()]),
          catchError((error) => {
            if (error.error && error.error.code === EMAIL_NOT_CONFIRMED_ERROR_CODE)
              return of(emailNeedsConfirmation({ email: error.error.email, authType: AuthNotificationType.SignIn }));
            return of(
              signinFailure({
                error: extractError(error, `An error occurred on http request`),
                silent: true,
              }),
            );
          }),
        ),
      ),
    ),
  );

  signup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signup),
      pluck('data'),
      switchMap((data) =>
        this.authService.signup(data).pipe(
          switchMap(() => [signupSuccess(), initUser()]),
          catchError((error) => {
            if (error.error && error.error.code === EMAIL_NOT_CONFIRMED_ERROR_CODE)
              return of(emailNeedsConfirmation({ email: error.error.email, authType: AuthNotificationType.SignUp }));
            return of(
              signupFailure({
                error: extractError(error, `An error occurred on http request`),
                silent: true,
              }),
            );
          }),
        ),
      ),
    ),
  );

  remindPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(remindPassword),
      pluck('email'),
      switchMap((email) =>
        this.authService.remindPassword(email).pipe(
          map(() => remindPasswordSuccess()),
          catchError((error) =>
            of(
              remindPasswordFailure({
                error: extractError(error, `An error occurred on http request`),
                silent: true,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPassword),
      pluck('data'),
      switchMap((data) =>
        this.authService.resetPassword(data).pipe(
          map(() => resetPasswordSuccess({ notification: 'Password reset' })),
          catchError((error) =>
            of(
              resetPasswordFailure({
                error: extractError(error, `An error occurred while resetting the password`),
                silent: true,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  sendActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendActivation),
      pluck('email'),
      switchMap((email) =>
        this.authService.resendActivation(email).pipe(
          map(() =>
            sendActivationSuccess({
              notification: `Confirmation sent to your email`,
            }),
          ),
          catchError((error) =>
            of(
              sendActivationFailure({
                error: extractError(error, `An error occurred while sending the confirmation mail`),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loginReroute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signinSuccess, signupSuccess),
        switchMap(() =>
          this.actions$.pipe(
            ofType(login, getUserFailure),
            take(1),
            filter((action) => action.type === login.type),
            tap(() => this.router.navigateByUrl('')), // triggers DefaultRouteGuard that redirects to proper route
          ),
        ),
      ),
    { dispatch: false },
  );

  logoutReroute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logoutSuccess),
        tap(() => this.router.navigateByUrl('auth/signin')),
      ),
    { dispatch: false },
  );

  remindPasswordReroute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(remindPasswordSuccess),
        switchMap(() =>
          timer(10000).pipe(
            tap(() => this.router.navigateByUrl('auth/signin')),
            takeUntil(this.router.events),
          ),
        ),
      ),
    { dispatch: false },
  );

  resetPasswordReroute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetPasswordSuccess),
        tap(() => this.router.url.startsWith('/auth/reset') && this.router.navigateByUrl('auth/signin')),
      ),
    { dispatch: false },
  );

  emailNeedsConfirmationReroute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(emailNeedsConfirmation),
        tap(({ email, authType }) =>
          this.router.navigate(['auth/confirmation'], { replaceUrl: true, queryParams: { email, type: authType } }),
        ),
      ),
    { dispatch: false },
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUser),
      switchMap(() =>
        this.authService.getUser().pipe(
          map((user) => getUserSuccess({ user })),
          catchError((error) =>
            of(
              getUserFailure({
                error: extractError(error, `Failed to get User Data`),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  clearClosedHintsContext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signinSuccess, signupSuccess),
      map(() => toggleBanner({ id: 'nebula-advertise', opened: true })),
    ),
  );
}
